import { Box } from '@mui/material';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { CountdownTimer } from './CountdownTimer';
import TimerExpiredModal from './TimerExpiredModal';
import { useLocationStore } from '@src/store/modules/location.store';
import { devLog } from '@src/utils/environment';
import { SessionManager } from '@src/utils/sessionManager';

/**
 * A persistent countdown timer that appears across all booking screens
 * after a timeslot has been selected. Timer resets if user selects a new slot.
 */
export const PersistentCountdownTimer: React.FC = () => {
  const router = useRouter();
  const {
    selectedLocation,
    setErrors,
    isWalkIn
  } = useLocationStore();
  const [locationId, setLocationId] = useState<string>('');
  const [showExpiredModal, setShowExpiredModal] = useState<boolean>(false);
  const [slotInfo, setSlotInfo] = useState<{
    date?: string;
    time?: string;
    provider?: string;
    location?: string;
  } | undefined>(undefined);
  useEffect(() => {
    // Only show timer if we have a selected location with a valid account ID
    if (selectedLocation?.revspringAccountId) {
      const accountId = selectedLocation.revspringAccountId;
      setLocationId(accountId);

      // Prepare slot info for tooltip
      if (selectedLocation) {
        // Extract slot information from the selected location
        // Use the available properties from the Location interface
        const offerDate = selectedLocation.offerDateTimeUTC ? new Date(selectedLocation.offerDateTimeUTC) : null;
        setSlotInfo({
          date: offerDate ? offerDate.toLocaleDateString() : '',
          time: offerDate ? offerDate.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit'
          }) : '',
          provider: selectedLocation.providerName || '',
          location: selectedLocation.name || ''
        });
      }

      // Debug logs
      devLog('PersistentCountdownTimer: Account ID set to', accountId);
      devLog('Has active hold?', SessionManager.hasActiveHold(accountId));
      if (SessionManager.hasActiveHold(accountId)) {
        devLog('Remaining seconds:', SessionManager.getHoldRemainingSeconds(accountId));
        devLog('Hold data:', SessionManager.getSession(accountId));
      } else {
        devLog('No active hold found for account ID:', accountId);
      }
    } else {
      devLog('PersistentCountdownTimer: No location selected with a valid account ID');
    }
  }, [selectedLocation]);

  // Handle timer expiration
  const handleExpire = () => {
    // Show modal instead of immediate redirect
    setShowExpiredModal(true);

    // Set errors in store (still useful for other components)
    setErrors([{
      errorTitle: 'Your selected time slot is no longer available',
      error: 'Your session has expired or the time slot is no longer available. Please select a new visit time.',
      errorLink: '/results'
    }]);
  };

  // Handle countdown click to navigate to patient info page
  const handleCountdownClick = () => {
    // Navigate to the patient info page if we have a valid location
    if (selectedLocation && locationId) {
      // Use the correct URL path for the patient page
      router.push('/appointment/');
    }
  };

  // Close modal handler
  const handleCloseModal = () => {
    setShowExpiredModal(false);
  };

  // Don't render if no locationId, no selected location, or if it's a walk-in visit
  if (!locationId || !selectedLocation || isWalkIn) {
    devLog('PersistentCountdownTimer: Not rendering because:', {
      noLocationId: !locationId,
      noSelectedLocation: !selectedLocation,
      isWalkIn,
      selectedLocationData: selectedLocation ? {
        name: selectedLocation.name,
        siteId: selectedLocation.siteId,
        revspringAccountId: selectedLocation.revspringAccountId,
        hasRevspringAccountId: 'revspringAccountId' in selectedLocation
      } : 'null'
    });
    return null;
  }

  // Debug whether there's an active hold
  const hasActiveHold = SessionManager.hasActiveHold(locationId);
  devLog(`PersistentCountdownTimer: Rendering with locationId ${locationId}, hasActiveHold: ${hasActiveHold}`);

  // If no active hold, explain why we're still showing (we shouldn't be)
  if (!hasActiveHold) {
    devLog('Warning: Timer component is being rendered but there is no active hold');
  }

  // Render the timer and modal (modal will only be visible when showExpiredModal is true)
  return <Box sx={{
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: 'fit-content',
    backgroundColor: '#f4f6f9!important'
  }} data-sentry-element="Box" data-sentry-component="PersistentCountdownTimer" data-sentry-source-file="PersistentCountdownTimer.tsx">
			<CountdownTimer locationId={locationId} onExpire={handleExpire} onClick={handleCountdownClick} slotInfo={slotInfo} data-sentry-element="CountdownTimer" data-sentry-source-file="PersistentCountdownTimer.tsx" />
			<TimerExpiredModal open={showExpiredModal} onClose={handleCloseModal} data-sentry-element="TimerExpiredModal" data-sentry-source-file="PersistentCountdownTimer.tsx" />
		</Box>;
};
export default PersistentCountdownTimer;